import { CheckIcon, MagnifyingGlassIcon, type Icon } from '@phosphor-icons/react';

import { Input } from '@/ui/Input';

/** The expanding selector panel: a search box over a scrollable list of rows. */
export function DropdownPanel({
  query,
  onQuery,
  placeholder,
  empty,
  isEmpty,
  prepend,
  children,
}: {
  query: string;
  onQuery: (v: string) => void;
  placeholder: string;
  empty: string;
  isEmpty: boolean;
  /** Rendered above the list regardless of {@link isEmpty} — e.g. a "create new" row. */
  prepend?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1.5 rounded-lg border border-line bg-surface-1 p-1.5 shadow-(--shadow-overlay)">
      <Input
        autoFocus
        inputSize={8}
        value={query}
        onChange={(e) => onQuery(e.target.value)}
        placeholder={placeholder}
        iconLeft={<MagnifyingGlassIcon size={15} />}
      />
      <div className="flex max-h-55 flex-col gap-px overflow-y-auto">
        {prepend}
        {isEmpty ? prepend ? null : <div className="px-2.5 py-3 text-xs text-fg-tertiary">{empty}</div> : children}
      </div>
    </div>
  );
}

/** A single repository / branch / model row inside a {@link DropdownPanel}. */
export function DropdownRow({
  icon: IconCmp,
  label,
  detail,
  selected,
  onClick,
}: {
  icon: Icon;
  label: string;
  /** Optional secondary line under the label, e.g. a model's per-token costs. */
  detail?: React.ReactNode;
  selected: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex w-full items-center justify-between gap-2.5 rounded-md px-2.5 py-2 transition-colors ${
        selected ? 'bg-brand-tint' : 'hover:bg-surface-2'
      }`}
    >
      <span className="flex min-w-0 items-center gap-2">
        <IconCmp size={16} className={`shrink-0 ${selected ? 'text-brand-text' : 'text-fg-tertiary'}`} />
        <span className="flex min-w-0 flex-col items-start">
          <span className={`truncate font-mono text-xs ${selected ? 'text-fg' : 'text-fg-secondary'}`}>{label}</span>
          {detail != null && <span className="truncate text-4xs text-fg-tertiary">{detail}</span>}
        </span>
      </span>
      {selected && <CheckIcon size={15} className="shrink-0 text-brand-text" />}
    </button>
  );
}
